// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// The per-site detail panel, opened from the map, the ruler, the beeswarm and
// the region cards.
//
// It is DETACHED from the DOM when closed rather than parked off-screen. A
// closed off-canvas panel at translateX(100%) is still a real box on iOS
// Safari: it scrolls the page sideways, and `overflow-x: clip` neither fixes it
// nor lets scrollWidth detect it.

import type { TowerData } from './types';
import { bandsOf, carrierOf, haversineKm } from './data';
import { CARRIER_ALIAS, CARRIER_COLOR, CLASS_COLOR, CLASS_LABEL, GEN_COLOR } from './colors';
import { attr, esc, km, mhz, num } from './format';
import { gloss } from './glossary';
import { trapFocus } from './views/ui';

let host: HTMLElement | null = null;
let onCloseCb: (() => void) | null = null;
// Held so EVERY close path tears them down — button, overlay, Escape, or a
// route change. Registering the key handler inside openSite and only removing
// it on Escape leaked one listener per open.
let releaseFocus: (() => void) | null = null;
let onKey: ((e: KeyboardEvent) => void) | null = null;

export function closeSite(notify = true): void {
  if (onKey) {
    document.removeEventListener('keydown', onKey);
    onKey = null;
  }
  if (releaseFocus) {
    releaseFocus();
    releaseFocus = null;
  }
  if (host) {
    host.remove();
    host = null;
  }
  if (notify && onCloseCb) onCloseCb();
}

export function onSiteClose(cb: () => void): void {
  onCloseCb = cb;
}

export function openSite(data: TowerData, i: number): void {
  closeSite(false);
  const s = data.sites;
  const carrier = carrierOf(data, s.mno[i]);
  const bands = bandsOf(data, s.mask[i]);
  const region = s.sa3[i] >= 0 ? data.regions[s.sa3[i]] : null;
  const fundIdx = s.fund[i];
  const program = fundIdx >= 0 ? s.programs[fundIdx] : null;

  const byClass = (cls: string) => bands.filter((b) => b.cls === cls);
  const chipRow = (cls: string) => {
    const list = byClass(cls);
    if (!list.length) {
      return `<p class="none">None — ${cls === 'low' ? 'this site cannot reach far' : cls === 'mid' ? 'no capacity layer here' : 'no millimetre wave'}</p>`;
    }
    return `<div class="chips">${list
      .map(
        (b) =>
          `<span class="chip" style="--c:${GEN_COLOR[b.gen]}" data-tip="${attr(`${b.id}\n${b.gen} on ${mhz(b.mhz)}\n${CLASS_LABEL[b.cls]}`)}">${esc(b.id)}<em>${esc(b.gen)}</em></span>`,
      )
      .join('')}</div>`;
  };

  // Distance to each carrier's nearest site from here — the "what else is
  // around" question, answered from the same table the ruler uses.
  const others = data.meta.carriers
    .map((c, ci) => {
      let best = Infinity;
      for (let j = 0; j < s.n; j++) {
        if (s.mno[j] !== ci || j === i) continue;
        const d = haversineKm(s.lat[i], s.lon[i], s.lat[j], s.lon[j]);
        if (d < best) best = d;
      }
      return { c, ci, km: best };
    })
    .filter((o) => Number.isFinite(o.km));

  host = document.createElement('div');
  host.className = 'drawer-host';
  host.innerHTML = `
    <div class="drawer-overlay" data-dclose></div>
    <aside class="drawer" role="dialog" aria-modal="true" aria-label="Mobile site detail">
      <header class="drawer-head" style="--c:${CARRIER_COLOR[carrier]}">
        <div>
          <span class="drawer-kicker">${esc(CARRIER_ALIAS[carrier])} · ${data.meta.latestYear}</span>
          <h2>Site ${esc(s.rfnsa[i] || '—')}</h2>
          <p class="drawer-sub">${region ? `${esc(region.name)}, ${esc(region.state)}` : 'Outside the mapped regions'}</p>
        </div>
        <button data-dclose class="drawer-close" aria-label="Close">✕</button>
      </header>
      <div class="drawer-body">
        ${program
          ? `<div class="flag flag-fund">${gloss('cofunded', 'Publicly co-funded')} — ${esc(program)}${s.round[i] ? ` · round ${esc(s.round[i])}` : ''}</div>`
          : `<div class="flag flag-comm">Built commercially — no public co-contribution recorded</div>`}

        <h3>${gloss('band', 'What this site transmits')}</h3>
        <div class="band-group">
          <h4 style="--c:${CLASS_COLOR.low}">${gloss('lowband', 'Long reach')} <span>under 1 GHz</span></h4>
          ${chipRow('low')}
        </div>
        <div class="band-group">
          <h4 style="--c:${CLASS_COLOR.mid}">${gloss('midband', 'Capacity')} <span>1.8–3.6 GHz</span></h4>
          ${chipRow('mid')}
        </div>
        ${byClass('mmwave').length
          ? `<div class="band-group"><h4 style="--c:${CLASS_COLOR.mmwave}">${gloss('mmwave', 'Millimetre wave')} <span>26 GHz</span></h4>${chipRow('mmwave')}</div>`
          : ''}

        <h3>Nearest site of each network</h3>
        <ul class="near-list">
          ${others
            .map(
              (o) =>
                `<li><span class="dot" style="background:${CARRIER_COLOR[o.c]}"></span>${esc(CARRIER_ALIAS[o.c])}<strong>${o.c === carrier ? `${km(o.km)} (same network)` : km(o.km)}</strong></li>`,
            )
            .join('')}
        </ul>

        <h3>Location</h3>
        <p class="coords">${s.lat[i].toFixed(5)}, ${s.lon[i].toFixed(5)}</p>
        ${region
          ? `<p class="muted">${esc(region.name)} covers ${num(region.areaKm2)} km² and ${num(region.pop)} residents, with ${num(region.total)} mobile sites across all three networks.</p>
             <button class="btn" data-region="${attr(region.code)}">Open this region's full comparison →</button>`
          : ''}
      </div>
    </aside>`;

  document.body.appendChild(host);
  releaseFocus = trapFocus(host.querySelector('.drawer') as HTMLElement, document.getElementById('app'));
  host.querySelectorAll('[data-dclose]').forEach((b) => b.addEventListener('click', () => closeSite()));
  host.querySelector('[data-region]')?.addEventListener('click', (e) => {
    const code = (e.currentTarget as HTMLElement).getAttribute('data-region');
    closeSite();
    location.hash = `view=region&sa3=${code}`;
  });
  onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') closeSite();
  };
  document.addEventListener('keydown', onKey);
}
